import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

type HintRequest = {
  id: symbol;
  priority: number;
  order: number;
};

type Queue = {
  register: (id: symbol, priority: number) => () => void;
  activeId: symbol | null;
};

const ActionHintQueueContext = createContext<Queue | null>(null);

export function ActionHintQueueProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [requests, setRequests] = useState<HintRequest[]>([]);
  const [activeId, setActiveId] = useState<symbol | null>(null);
  const order = useRef(0);

  // Keep the visible hint until it leaves the queue. Then select the highest
  // priority request, using registration order to break ties.
  const selectedId =
    (activeId && requests.some(request => request.id === activeId)
      ? activeId
      : [...requests].sort(
          (a, b) => b.priority - a.priority || a.order - b.order
        )[0]?.id) ?? null;

  useEffect(() => {
    setActiveId(selectedId);
  }, [selectedId]);

  const register = useCallback((id: symbol, priority: number) => {
    setRequests(current => [
      ...current.filter(request => request.id !== id),
      { id, priority, order: order.current++ },
    ]);
    return () => {
      setRequests(current => current.filter(request => request.id !== id));
    };
  }, []);

  const value = useMemo(
    () => ({ register, activeId: selectedId }),
    [register, selectedId]
  );

  return (
    <ActionHintQueueContext.Provider value={value}>
      {children}
    </ActionHintQueueContext.Provider>
  );
}

export function useQueuedActionHint(
  priority: number,
  requested: boolean,
  storageKey?: string
) {
  const queue = useContext(ActionHintQueueContext);
  if (!queue)
    throw new Error('useQueuedActionHint requires ActionHintQueueProvider');

  // A different hint in the same popover must take a new place in the queue.
  const id = useMemo(() => Symbol(`action-hint-${storageKey}`), [storageKey]);
  const { register, activeId } = queue;
  useEffect(() => {
    if (requested) return register(id, priority);
    return undefined;
  }, [id, priority, register, requested]);

  return requested && activeId === id;
}
